// A path router small enough to read in one sitting: the server serves index.html for every
// non-API path (see Program.cs), so /login, /connect and /licenses/{id} are real urls.
import { useEffect, useState } from 'react'

export interface Route {
  /** The path without a trailing slash; "/" for the root. */
  path: string
  query: URLSearchParams
}

const listeners = new Set<() => void>()

function read(): Route {
  return {
    path: window.location.pathname.replace(/\/+$/, '') || '/',
    query: new URLSearchParams(window.location.search),
  }
}

export function navigate(to: string, replace = false): void {
  if (replace) window.history.replaceState(null, '', to)
  else window.history.pushState(null, '', to)
  for (const listener of listeners) listener()
}

export function useRoute(): Route {
  const [route, setRoute] = useState(read)
  useEffect(() => {
    const update = () => setRoute(read())
    listeners.add(update)
    window.addEventListener('popstate', update)
    return () => {
      listeners.delete(update)
      window.removeEventListener('popstate', update)
    }
  }, [])
  return route
}

/** The current path and query as one string, for a returnUrl. */
export function currentUrl(): string {
  return window.location.pathname + window.location.search
}

/** A local path the app is willing to go to after a sign-in; anything else becomes "/". */
export function safeReturnUrl(value: string | null | undefined): string {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : '/'
}
